import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core'
import type { WorkBook, WorkSheet } from 'xlsx'
import {
  asyncForEach,
  SchemaUtil,
  detectUTF8,
  UI_langs,
  Possible_Encodings,
  fileTemplatesURL,
  loadXlsx,
  buildImportColumnMap,
} from '../../../utils'
import { ModalContainerComponent } from '../../_common/modal-container/modal-container.component'
import { Address, Carrier, Checkout, Order, OrderItem, Payment, PaymentItem, Product } from 'depoto-core/src/entities'
import { CoreService, NotificationService } from '../../../services'
import { TranslateService } from '@ngx-translate/core'
import * as chardet from 'chardet'
import { injectQuery } from '@tanstack/angular-query-experimental'
import { VatService } from '../../../services/entity-services/vat.services'
import { PaymentService } from '../../../services/entity-services/payment.service'

type ImportSource = {
  sheetName?: string
  rowNumber?: number
  reservationNumberCell?: string
  carrierCell?: string
  paymentTypeCell?: string
  currencyCell?: string
}

type ImportIssueKind = 'file' | 'import'
type ProductSearchField = 'ean' | 'code' | 'fulltext'

type ImportIssue = {
  kind: ImportIssueKind
  message: string
}

@Component({
  selector: 'modal-orders-import',
  templateUrl: 'modal-orders-import.component.html',
  styleUrls: ['modal-orders-import.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalOrdersImportComponent {
  private vatService = inject(VatService)
  private paymentService = inject(PaymentService)
  @ViewChild('modal') public childModal: ModalContainerComponent
  @Input() public carriers: Carrier[] = []
  @Input() public checkouts: Checkout[] = []
  @Input() public payments: Payment[] = []
  @Input() dataCyAttribute: string
  @Output() public onOrdersImported: EventEmitter<Array<Order>> = new EventEmitter()
  loadedFileEv: any
  orders: Order[] = []
  fileTypes: string[] = ['depoto-orders.xlsx', 'go-orders.csv', 'flexibee-order.xml']
  selectedFileType = 'depoto-orders.xlsx'
  goPaymentId: string = 'cash_on_delivery' // Dobírka // todo: ok for go, select for depoto-orders.xlsx
  goPayment2Id: string = 'bank_account' // Prevodem // todo: ok for go, select for depoto-orders.xlsx
  selectedCheckout: number
  isImportFileLoaded = false
  templatesURL = fileTemplatesURL
  progress = 0
  finalMessage = ''
  finalMessageClass = 'alert-success'
  importedCount = 0
  loading = false
  skippedOrders: string[] = []
  skippedProducts = []
  orderImportSources: Record<string, ImportSource> = {}
  defaultColumnTypesCS = {
    // a property must totally coincide with the template
    reservationNumber: 'Číslo objednávky',
    ean: 'EAN produktu',
    code: 'Kód produktu',
    productName: 'Název Produktu',
    quantity: 'Množství',
    firstName: 'Jméno',
    lastName: 'Příjmení',
    street: 'Ulice',
    city: 'Město',
    zip: 'PSČ',
    country: 'Země',
    phone: 'Telefon',
    email: 'E-mail',
    paymentType: 'Platba - typ',
    paymentAmount: 'Platba - Částka',
    currency: 'Měna',
    carrier: 'Dopravce',
    note: 'Poznámka',
  }
  defaultColumnTypesEN = {
    reservationNumber: 'Order number',
    ean: 'Product EAN',
    code: 'Product SKU',
    productName: 'Product name',
    quantity: 'Quantity',
    firstName: 'Name',
    lastName: 'Last Name',
    street: 'Address', // as in the template
    city: 'City',
    zip: 'ZIP',
    country: 'Country code',
    phone: 'Phone',
    email: 'E-mail',
    paymentType: 'Payment type',
    paymentAmount: 'Payment price',
    currency: 'Currency',
    carrier: 'Delivery method',
    note: 'Note',
  }
  columnTypes: Record<string, unknown>[] = []
  importMap: any = {
    reservationNumber: -1,
    ean: -1,
    code: -1,
    productName: -1,
    quantity: -1,
    firstName: -1,
    lastName: -1,
    street: -1,
    city: -1,
    zip: -1,
    country: -1,
    // branchId: 11,
    phone: -1,
    email: -1,
    paymentType: -1,
    paymentAmount: -1,
    currency: -1,
    carrier: -1,
    note: -1,
  }
  isImportMapSet = false
  isImportMapUpdating = false
  uintArray: Uint8Array | null = null
  errors: ImportIssue[] = []
  hasImportRun = false

  readonly queryVats = injectQuery(() => ({
    queryKey: ['vats', 'options'],
    queryFn: async () => {
      return await this.vatService.getVats({}, { id: null, name: null, percent: null, default: null }).then(result => {
        return result.items
      })
    },
  }))

  readonly queryCurrencies = injectQuery(() => ({
    queryKey: ['currencies', 'options'],
    queryFn: async () => {
      return await this.paymentService.getCurrencies({}, { id: null, name: null, ratio: null }).then(result => {
        return result.items
      })
    },
  }))

  constructor(
    private core: CoreService,
    private translateService: TranslateService,
    private notificationService: NotificationService
  ) {
    effect(() => {
      this.queryVats.data()
      this.queryCurrencies.data()
    })
  }

  private resetImportResult(clearErrors = true): void {
    this.skippedOrders = []
    this.skippedProducts = []
    this.orderImportSources = {}
    this.importedCount = 0
    this.finalMessage = ''
    this.finalMessageClass = 'alert-success'
    this.hasImportRun = false
    this.progress = 0
    if (clearErrors) {
      this.errors = []
    }
  }

  get fileIssues(): ImportIssue[] {
    return this.errors.filter(issue => issue.kind === 'file')
  }

  get importIssues(): ImportIssue[] {
    return this.errors.filter(issue => issue.kind === 'import')
  }

  showChildModal(): void {
    this.loadedFileEv = null
    this.isImportFileLoaded = false
    this.orders = []
    this.selectedCheckout = undefined
    this.resetImportResult()
    this.isImportMapSet = false
    this.isImportMapUpdating = false
    this.childModal.showChildModal()
    if (this.checkouts && this.checkouts.length === 1) {
      this.selectedCheckout = this.checkouts[0].id
    }
  }

  hideChildModal(): void {
    this.childModal.hideChildModal()
  }

  loadFile(event): void {
    this.loadedFileEv = event
  }

  async handleImportFile(): Promise<any> {
    if (!this.loadedFileEv) {
      alert(this.translateService.instant('modal-orders-import.no-file'))
      return
    }
    if (!this.selectedCheckout) {
      alert(this.translateService.instant('modal-orders-import.select-checkout'))
      return
    }
    this.loading = true
    this.resetImportResult()
    let arrayBuff = null
    const isXls = this.loadedFileEv.target.value.endsWith('.xlsx')
    const isXml = this.loadedFileEv.target.value.endsWith('.xml')
    const isCsv = this.loadedFileEv.target.value.endsWith('.csv')
    if (
      (!isXls && !isCsv && !isXml) ||
      (isXls && this.selectedFileType != 'depoto-orders.xlsx') ||
      (isCsv && this.selectedFileType != 'go-orders.csv') ||
      (isXml && this.selectedFileType != 'flexibee-order.xml')
    ) {
      alert(this.translateService.instant('modal-orders-import.wrong-format'))
      this.loading = false
      return
    }
    try {
      arrayBuff = await this.getDataFromImportedFile(this.loadedFileEv)
      this.uintArray = new Uint8Array(arrayBuff)
      const encoding = chardet
        .analyse(this.uintArray)
        .filter(x => Possible_Encodings.includes(x.name))
        .sort((a, b) => b.confidence - a.confidence)[0].name

      console.log('Detected:', detectUTF8(this.uintArray), encoding == 'UTF-8' ? '' : encoding)
      let binaryString = isXls ? null : new TextDecoder(encoding).decode(this.uintArray)

      if (isCsv) {
        binaryString = binaryString.replace(/=CONCATENATE\(|\)/g, '')
      }
      let parsedOrders: Order[] = []
      if (isXls) {
        parsedOrders = await this.parseXlsDepoto(this.uintArray)
        /*  this.selectedFileType === 'depoto-orders.xlsx'
            ? await this.parseXlsDepoto(this.uintArray)
            : await this.parseXls(this.uintArray) */
      }
      if (isXml) {
        parsedOrders = await this.parseFlexibeeXml(binaryString)
      }
      if (isCsv) {
        parsedOrders = await this.parseCsv(binaryString)
      }
      this.orders = parsedOrders
      if (isCsv || isXml) {
        this.isImportFileLoaded = true
      }
    } catch (err) {
      alert(
        typeof err === 'string'
          ? err
          : err.message
            ? err.message === "Cannot read property 'childNodes' of undefined"
              ? this.translateService.instant('modal-orders-import.import.error')
              : err.message
            : JSON.stringify(err)
      )
    } finally {
      this.loading = false
    }
  }

  getDataFromImportedFile(inputEvent: Event): Promise<string | any> {
    const target = inputEvent.target as HTMLInputElement
    return new Promise((resolve, reject) => {
      const file = target.files[0]
      if (file) {
        const reader = new FileReader()
        reader.readAsArrayBuffer(file)
        reader.onload = evt => {
          resolve(evt.target.result)
        }
        reader.onerror = () => {
          reject(this.translateService.instant('file-processing-error', { name: file.name }))
        }
      } else {
        reject(this.translateService.instant('no-file'))
      }
    })
  }

  async setImportMapAndContinue() {
    this.loading = true
    this.isImportMapUpdating = false
    this.isImportMapSet = true
    this.resetImportResult()
    try {
      this.orders = await this.parseXlsDepoto(this.uintArray)
      this.isImportFileLoaded = true
    } catch (err) {
      this.addImportIssue(
        'file',
        this.getErrorMessages(err).join('. ') || this.translateService.instant('modal-orders-import.import.error')
      )
      this.isImportMapUpdating = true
      this.isImportMapSet = false
      this.isImportFileLoaded = false
    } finally {
      this.loading = false
    }
  }

  async import() {
    const results: Order[] = []
    this.skippedOrders = []
    this.progress = 0
    this.finalMessage = ''
    this.finalMessageClass = 'alert-success'
    this.importedCount = 0
    this.loading = true
    const previousAvoidErrorAlerts = this.notificationService.avoidErrorAlerts
    this.notificationService.avoidErrorAlerts = true
    let x = 1
    try {
      for (const o of this.orders) {
        try {
          if (!o.reservationNumber) {
            this.addImportIssue(
              'file',
              this.formatImportIssue(
                o,
                this.translateService.instant('modal-orders-import.no-reservation'),
                'reservationNumberCell'
              )
            )
            continue
          }
          // TODO CORE QUERY
          const existing = await this.core.services.order.getList(
            { filters: { reservationNumber: Number(o.reservationNumber) } },
            { id: null }
          )
          if (existing?.items?.length) {
            this.skippedOrders.push(
              this.formatImportIssue(
                o,
                this.translateService.instant('modal-orders-import.order-already-exists'),
                'reservationNumberCell'
              )
            )
            continue
          }
          const carrierError = this.getInvalidCarrierMessage(o)
          if (carrierError) {
            this.addImportIssue('file', this.formatImportIssue(o, carrierError, 'carrierCell'))
            continue
          }
          o.shippingAddress = await this.runImportCreate('createAddress', () =>
            this.core.services.address.create(o.shippingAddress)
          ) // TODO CORE MUTATION
          o.invoiceAddress = o.shippingAddress
          o.checkout = new Checkout()
          o.checkout.id = Number(this.selectedCheckout)
          const withPayment = o.paymentItems.filter(p => p.payment)
          if (!withPayment.length && this.selectedFileType != 'flexibee-order.xml') {
            this.addImportIssue(
              'file',
              this.formatImportIssue(
                o,
                this.translateService.instant('failed.to.import.payment.0') +
                  o.reservationNumber +
                  this.translateService.instant('failed.to.import.payment.1') +
                  (o.paymentItems[0]?.payment ?? '') +
                  this.translateService.instant('failed.to.import.payment.2'),
                'paymentTypeCell'
              )
            )
          } else {
            o.paymentItems = withPayment
            const res = await this.runImportCreate('createOrder', () => this.core.services.order.create(o)) // TODO CORE MUTATION
            results.push(res)
          }
        } catch (e) {
          const message =
            this.getErrorMessages(e).join('. ') || this.translateService.instant('modal-orders-import.import.error')
          this.addImportIssue('import', this.formatImportIssue(o, message, this.getSourceKeyForErrorMessage(message)))
          console.error(e)
        } finally {
          this.progress = this.orders.length ? x / (this.orders.length / 100) : 100
          x++
        }
      }
    } finally {
      this.loading = false
      this.notificationService.avoidErrorAlerts = previousAvoidErrorAlerts
    }
    this.hasImportRun = true
    this.importedCount = results.length
    this.setFinalImportMessage()
    if (results.length > 0) {
      this.onOrdersImported.emit(results)
    }
    this.isImportFileLoaded = false
  }

  private async runImportCreate<T>(method: string, create: () => Promise<T>): Promise<T> {
    const eventErrors: string[] = []
    const observer = this.core.services.baseEvent.subscribe()
    observer.then((event: any) => {
      if (event?.type === 'reject' && event?.method === method) {
        eventErrors.push(...this.getErrorMessages(event.errors))
      }
    })
    try {
      const result = await create()
      if (!result && eventErrors.length > 0) {
        throw new Error(this.uniqueMessages(eventErrors).join('. '))
      }
      if (!result) {
        throw new Error(this.translateService.instant('modal-orders-import.import.error'))
      }
      return result
    } catch (e) {
      const messages = this.uniqueMessages([...eventErrors, ...this.getErrorMessages(e)])
      throw new Error(messages.join('. ') || this.translateService.instant('modal-orders-import.import.error'))
    } finally {
      observer.unsubscribe()
    }
  }

  private setFinalImportMessage(): void {
    const issueCount = this.errors.length + this.skippedOrders.length
    if (issueCount === 0) {
      this.finalMessage = this.translateService.instant('import.finished')
      this.finalMessageClass = 'alert-success'
      return
    }
    if (this.importedCount > 0) {
      this.finalMessage = this.translateService.instant('modal-orders-import.partial-finished', {
        imported: this.importedCount,
        failed: issueCount,
      })
      this.finalMessageClass = 'alert-warning'
      return
    }
    this.finalMessage = this.translateService.instant('modal-orders-import.failed-finished', { failed: issueCount })
    this.finalMessageClass = 'alert-danger'
  }

  private getInvalidCarrierMessage(order: Order): string | null {
    const carrierId = this.normalizeImportId((order.carrier as Carrier)?.id)
    if (!carrierId) {
      return null
    }
    const allowedCarrierIds = new Set(
      (this.carriers ?? []).map(carrier => this.normalizeImportId(carrier?.id)).filter(id => id && id !== 'all')
    )
    if (!allowedCarrierIds.size || allowedCarrierIds.has(carrierId)) {
      return null
    }
    return this.translateService.instant('modal-orders-import.invalid-carrier', {
      carrier: (order.carrier as Carrier).id,
    })
  }

  private normalizeImportId(value: unknown): string {
    return this.castImportCellToString(value).trim().toLowerCase()
  }

  private castImportCellToString(value: unknown): string {
    return typeof value === 'string' ? value : typeof value === 'number' ? value.toFixed(0) : value ? `${value}` : ''
  }

  private formatImportIssue(order: Order, message: string, sourceKey?: keyof ImportSource): string {
    const reservationNumber = order?.reservationNumber
    const orderLabel = reservationNumber
      ? `${this.translateService.instant('orders.number')}${reservationNumber}`
      : this.translateService.instant('modal-orders-import.unknown-order')
    const source = this.getImportSourceText(reservationNumber, sourceKey)
    return source ? `${orderLabel} (${source}): ${message}` : `${orderLabel}: ${message}`
  }

  private getImportSourceText(reservationNumber: number | string, sourceKey?: keyof ImportSource): string {
    const source = this.orderImportSources[this.getOrderSourceKey(reservationNumber)]
    if (!source) {
      return ''
    }
    const cell = sourceKey ? source[sourceKey] : null
    if (cell) {
      const cellAddress = source.sheetName ? `${source.sheetName}!${cell}` : cell
      return this.translateService.instant('modal-orders-import.source.cell', {
        row: source.rowNumber,
        cell: cellAddress,
      })
    }
    if (source.rowNumber) {
      return this.translateService.instant('modal-orders-import.source.row', { row: source.rowNumber })
    }
    return ''
  }

  private getOrderSourceKey(reservationNumber: number | string): string {
    return `${reservationNumber ?? ''}`
  }

  private getSourceKeyForErrorMessage(message: string): keyof ImportSource | undefined {
    const lowerMessage = message.toLowerCase()
    if (lowerMessage.includes('carrier') || lowerMessage.includes('doprav')) {
      return 'carrierCell'
    }
    if (lowerMessage.includes('payment') || lowerMessage.includes('platb')) {
      return 'paymentTypeCell'
    }
    return undefined
  }

  private getErrorMessages(error: any): string[] {
    if (!error) {
      return []
    }
    if (Array.isArray(error)) {
      return error.flatMap(e => this.getErrorMessages(e))
    }
    if (typeof error === 'string') {
      return [error]
    }
    if (error.message) {
      return [error.message]
    }
    if (error.errorMessage) {
      return [error.errorMessage]
    }
    try {
      return [JSON.stringify(error)]
    } catch {
      return [`${error}`]
    }
  }

  private uniqueMessages(messages: string[]): string[] {
    return [...new Set(messages.filter(message => !!message))]
  }

  private addImportIssue(kind: ImportIssueKind, message: string): void {
    this.errors.push({ kind, message })
  }

  private findImportProduct(field: ProductSearchField, value: unknown): Promise<Product | null> {
    const normalizedValue = this.castImportCellToString(value).trim()
    if (!normalizedValue) {
      return Promise.resolve(null)
    }
    return this.core.services.product
      .getList(
        { filters: { [field]: normalizedValue } },
        { ...SchemaUtil.productImportSearch, quantities: SchemaUtil.quantity }
      )
      .then(result => result?.items?.[0] ?? null)
      .catch(() => null)
  }

  protected async parseXlsDepoto(typedArray: Uint8Array): Promise<Array<Order>> {
    const XLSX = await loadXlsx()
    const wb: WorkBook = XLSX.read(typedArray, { cellDates: true })
    const orders: any = {}
    const parsedOrders: Order[] = []
    const invalidOrderNumbers = new Set<string>()
    this.progress = 0
    this.finalMessage = ''
    await asyncForEach(wb.SheetNames, async sheetName => {
      const ws: WorkSheet = wb.Sheets[sheetName]
      const data: Array<Array<any>> | any = XLSX.utils.sheet_to_json(ws, { header: 1 })
      const castToStr = val =>
        typeof val === 'string' ? val : typeof val === 'number' ? val.toFixed(0) : val ? val : ''
      await asyncForEach(data, async (n, i) => {
        if (n?.length > 0) {
          if (i === 0 && !this.isImportMapSet) {
            const headings = n.map(c => castToStr(c))
            this.columnTypes = [{ id: 'none', name: ' ' }, ...headings.map((h, index) => ({ id: index, name: h }))]
            const defaultColumnsByLang = UI_langs.map(lang => this['defaultColumnTypes' + lang])
            this.importMap = buildImportColumnMap(headings, defaultColumnsByLang)
            this.isImportMapUpdating = true
            return
          } else if (i > 0 && this.isImportMapSet) {
            const importMap = this.importMap
            const rowNumber = castToStr(n[importMap.reservationNumber])
            const getCellAddress = (columnIndex: number) =>
              columnIndex >= 0 ? XLSX.utils.encode_cell({ r: i, c: columnIndex }) : undefined
            const sourceKey = this.getOrderSourceKey(rowNumber)
            if (!this.orderImportSources[sourceKey]) {
              this.orderImportSources[sourceKey] = {
                sheetName,
                rowNumber: i + 1,
                reservationNumberCell: getCellAddress(importMap.reservationNumber),
                carrierCell: getCellAddress(importMap.carrier),
                paymentTypeCell: getCellAddress(importMap.paymentType),
                currencyCell: getCellAddress(importMap.currency),
              }
            }
            if (!rowNumber) {
              const orderWithoutNumber = new Order()
              this.addImportIssue(
                'file',
                this.formatImportIssue(
                  orderWithoutNumber,
                  this.translateService.instant('modal-orders-import.no-reservation'),
                  'reservationNumberCell'
                )
              )
              return
            }
            if (invalidOrderNumbers.has(rowNumber)) {
              return
            }

            if (!orders[rowNumber]) {
              orders[rowNumber] = new Order()
            }
            const isFirstOrderRow = !orders[rowNumber].shippingAddress
            if (isFirstOrderRow && importMap.carrier >= 0) {
              orders[rowNumber].reservationNumber = rowNumber
              orders[rowNumber].carrier = new Carrier({ id: castToStr(n[importMap.carrier]).toLowerCase() })
              const carrierError = this.getInvalidCarrierMessage(orders[rowNumber])
              if (carrierError) {
                this.addImportIssue('file', this.formatImportIssue(orders[rowNumber], carrierError, 'carrierCell'))
                invalidOrderNumbers.add(rowNumber)
                delete orders[rowNumber]
                return
              }
            }
            if (isFirstOrderRow && importMap.currency >= 0) {
              const currencyCode = castToStr(n[importMap.currency]).trim()
              if (currencyCode) {
                const currency = (this.queryCurrencies.data() ?? []).find(
                  c => c.id?.toLowerCase() === currencyCode.toLowerCase()
                )
                if (!currency) {
                  orders[rowNumber].reservationNumber = rowNumber
                  this.addImportIssue(
                    'file',
                    this.formatImportIssue(
                      orders[rowNumber],
                      this.translateService.instant('modal-orders-import.invalid-currency', { currency: currencyCode }),
                      'currencyCell'
                    )
                  )
                  invalidOrderNumbers.add(rowNumber)
                  delete orders[rowNumber]
                  return
                }
                orders[rowNumber].currency = currency
              }
            }

            // depoto-core always sends `vat: item.vat?.id`, so a missing VAT would produce `vat:,` in the mutation
            const defaultVat = (this.queryVats.data() ?? []).find(v => v.default)
            if (!defaultVat) {
              orders[rowNumber].reservationNumber = rowNumber
              this.addImportIssue(
                'file',
                this.formatImportIssue(
                  orders[rowNumber],
                  this.translateService.instant('modal-orders-import.missing-default-vat')
                )
              )
              invalidOrderNumbers.add(rowNumber)
              delete orders[rowNumber]
              return
            }

            const eanOrCodeOrName =
              importMap.ean >= 0
                ? 'ean'
                : importMap.code >= 0
                  ? 'code'
                  : importMap.productName >= 0
                    ? 'productName'
                    : ''
            let product: Product | null = null
            if (eanOrCodeOrName) {
              const productSearchField =
                eanOrCodeOrName === 'productName' ? 'fulltext' : (eanOrCodeOrName as ProductSearchField)
              product = await this.findImportProduct(productSearchField, n[importMap[eanOrCodeOrName]])
            }

            // Check if imported quantity is available on selected checkout's depots when the product exists.
            const selectedCheckout = this.checkouts.find(ch => Number(ch.id) === Number(this.selectedCheckout))
            if (product && selectedCheckout?.depots?.length) {
              const availableQuantityFromDepots = selectedCheckout.depots.reduce((total, depot) => {
                const quant = product.quantities?.find(q => Number(q.depot?.id) === Number(depot.id))
                return total + (quant?.quantityAvailable ?? 0)
              }, 0)
              if (availableQuantityFromDepots < Number(n[importMap.quantity])) {
                this.skippedProducts.push({
                  orderItemName: product.fullName || castToStr(n[importMap.productName]),
                  ean: product.ean,
                  orderQuantity: Number(n[importMap.quantity]),
                  quantityAvailable: availableQuantityFromDepots,
                })
              }
            }

            const oItem = new OrderItem({
              name: (product?.fullName || n[importMap.productName]) ?? '',
              quantity: Number(n[importMap.quantity]),
              vat: defaultVat,
              price: Number(n[importMap.paymentAmount]) || 0,
              priceAll: Number(n[importMap.paymentAmount]) * Number(n[importMap.quantity]) || 0,
              product,
            })
            if (importMap.ean >= 0) {
              oItem.ean = `${n[importMap.ean]}`
            }
            if (importMap.code >= 0) {
              oItem.code = `${n[importMap.code]}`
            }
            orders[rowNumber].items.push(oItem)
            // address, note etc on first row of multiitem orders
            if (!orders[rowNumber].shippingAddress) {
              orders[rowNumber].dateCreated = new Date().toISOString()
              // orders[rowNumber].billNumber = rowNumber
              orders[rowNumber].reservationNumber = rowNumber
              if (importMap.carrier >= 0) {
                orders[rowNumber].carrier = new Carrier({ id: castToStr(n[importMap.carrier]).toLowerCase() })
              }
              if (importMap.note >= 0) {
                orders[rowNumber].note = castToStr(n[importMap.note])
              }
              orders[rowNumber].shippingAddress = new Address({
                firstName: castToStr(n[importMap.firstName]),
                lastName: castToStr(n[importMap.lastName]),
                street: castToStr(n[importMap.street]),
                city: castToStr(n[importMap.city]),
                zip: castToStr(n[importMap.zip]),
                phone: castToStr(n[importMap.phone]),
                email: castToStr(n[importMap.email]),
                country: castToStr(n[importMap.country]),
              })
              const paymentItem = new PaymentItem({
                payment: this.payments.find(p => p.type?.id === n[importMap.paymentType]),
                checkout: this.checkouts.filter(c => c.id === this.selectedCheckout)[0],
                amount: Number(n[importMap.paymentAmount]) || 0,
              })
              orders[rowNumber].paymentItems = [paymentItem]
              if (!paymentItem.payment?.type?.id) {
                this.addImportIssue(
                  'file',
                  this.formatImportIssue(
                    orders[rowNumber],
                    this.translateService.instant('failed.to.import.payment.0') +
                      orders[rowNumber].reservationNumber +
                      this.translateService.instant('failed.to.import.payment.1') +
                      n[importMap.paymentType] +
                      this.translateService.instant('failed.to.import.payment.2'),
                    'paymentTypeCell'
                  )
                )
                invalidOrderNumbers.add(rowNumber)
                delete orders[rowNumber]
              }
            }
          }
        }
        this.progress = (i + 1) / (data.length / 100)
      })
    })
    Object.keys(orders).forEach(k => {
      if (orders[k].paymentItems.filter(p => p.payment).length) {
        parsedOrders.push(orders[k])
      }
    })
    return parsedOrders
  }

  async parseFlexibeeXml(str) {
    const getNode = (tag, nodes) => {
      for (const n of nodes) {
        if (n.tagName === tag) {
          return n
        }
      }
    }
    const res = []
    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(str, 'application/xml')

    const invoices = Array.from(xmlDoc.getElementsByTagName('faktura-vydana'))
    for (const invoice of invoices) {
      const order = new Order()
      order.reservationNumber = Number(getNode('varSym', invoice.childNodes).innerHTML)
      const items = getNode('polozkyFaktury', invoice.childNodes).childNodes
      for (const item of items) {
        if (item.tagName === 'faktura-vydana-polozka') {
          const orderItem = new OrderItem()
          orderItem.product = new Product()
          orderItem.name = getNode('nazev', item.childNodes)?.innerHTML || ''
          orderItem.product.fullName = getNode('nazev', item.childNodes)?.innerHTML || ''
          orderItem.code = getNode('kod', item.childNodes)?.innerHTML || null
          orderItem.product.code = getNode('kod', item.childNodes)?.innerHTML || null
          orderItem.ean = getNode('eanKod', item.childNodes)?.innerHTML || null
          orderItem.product.ean = getNode('eanKod', item.childNodes)?.innerHTML || null
          orderItem.quantity = Number(getNode('mnozMj', item.childNodes)?.innerHTML || 1)
          orderItem.price = Number(getNode('sumCelkem', item.childNodes)?.innerHTML || 0) / orderItem.quantity
          orderItem.priceWithoutVat = Number(getNode('sumZkl', item.childNodes)?.innerHTML || 0) / orderItem.quantity
          orderItem.priceAll = Number(getNode('sumCelkem', item.childNodes)?.innerHTML || 0)
          orderItem.vat = (this.queryVats.data() ?? []).find(v => v.default)
          // orderItem.vat = this.core.lists.vats.find(v => v.default) // TODO
          if (orderItem.price === -Infinity || orderItem.price === Infinity) {
            orderItem.price = 0
          }
          if (orderItem.priceWithoutVat === -Infinity || orderItem.priceWithoutVat === Infinity) {
            orderItem.priceWithoutVat = 0
          }
          if (orderItem.priceAll === -Infinity || orderItem.priceAll === Infinity) {
            orderItem.priceAll = 0
          }
          const product = orderItem.ean
            ? await this.findImportProduct('ean', orderItem.ean)
            : orderItem.code
              ? await this.findImportProduct('code', orderItem.code)
              : orderItem.name
                ? await this.findImportProduct('fulltext', orderItem.name)
                : null
          if (product) {
            orderItem.product = product
          }
          order.items.push(orderItem)
        }
      }
      order.shippingAddress = new Address({
        firstName: getNode('nazFirmy', invoice.childNodes)?.innerHTML || '',
        street: getNode('ulice', invoice.childNodes)?.innerHTML || '',
        city: getNode('mesto', invoice.childNodes)?.innerHTML || '',
        zip: getNode('psc', invoice.childNodes)?.innerHTML || '',
        phone: getNode('kontaktTel', invoice.childNodes)?.innerHTML || '',
        country: getNode('stat', invoice.childNodes)?.innerHTML?.replace('code:', '') || 'CZ',
      })
      const isInvoiceSameAsShipping = getNode('postovniShodna', invoice.childNodes)?.innerHTML === 'true'
      if (!isInvoiceSameAsShipping) {
        order.invoiceAddress = new Address({
          firstName: getNode('faNazev', invoice.childNodes)?.innerHTML || '',
          street: getNode('faUlice', invoice.childNodes)?.innerHTML || '',
          city: getNode('faMesto', invoice.childNodes)?.innerHTML || '',
          zip: getNode('faPsc', invoice.childNodes)?.innerHTML || '',
          country: getNode('faStat', invoice.childNodes)?.innerHTML?.replace('code:', '') || 'CZ',
        })
      }
      res.push(order)
    }
    return res
  }

  async parseCsv(str) {
    const arrOfOrdersInArrays = []
    let quote = false // 'true' means we're inside a quoted field
    const delimeter = str.indexOf(';') > 0 && (str.indexOf(',') < 0 || str.indexOf(';') < str.indexOf(',')) ? ';' : ','

    // Iterate over each character, keep track of current row and column (of the returned array)
    for (let row = 0, col = 0, c = 0; c < str.length; c++) {
      const cc = str[c],
        nc = str[c + 1] // Current character, next character
      arrOfOrdersInArrays[row] = arrOfOrdersInArrays[row] || [] // Create a new row if necessary
      arrOfOrdersInArrays[row][col] = arrOfOrdersInArrays[row][col] || '' // Create a new column (start with empty string) if necessary

      // If the current character is a quotation mark, and we're inside a
      // quoted field, and the next character is also a quotation mark,
      // add a quotation mark to the current column and skip the next character
      if (cc === '"' && quote && nc === '"') {
        arrOfOrdersInArrays[row][col] += cc
        ++c
        continue
      }

      // If it's just one quotation mark, begin/end quoted field
      if (cc === '"') {
        quote = !quote
        continue
      }

      // If it's a delimeter and we're not in a quoted field, move on to the next column
      if (cc === delimeter && !quote) {
        ++col
        continue
      }

      // If it's a newline (CRLF) and we're not in a quoted field, skip the next character
      // and move on to the next row and move to column 0 of that new row
      if (cc === '\r' && nc === '\n' && !quote) {
        ++row
        col = 0
        ++c
        continue
      }

      // If it's a newline (LF or CR) and we're not in a quoted field,
      // move on to the next row and move to column 0 of that new row
      if (cc === '\n' && !quote) {
        ++row
        col = 0
        continue
      }
      if (cc === '\r' && !quote) {
        ++row
        col = 0
        continue
      }

      // Otherwise, append the current character to the current column
      arrOfOrdersInArrays[row][col] += cc
    }

    const orders = []
    for (const orderPropertiesInArray of arrOfOrdersInArrays) {
      const index = arrOfOrdersInArrays.indexOf(orderPropertiesInArray)
      // skip first line -> header
      if (index === 0) continue

      const newOrder: Order = new Order({})
      const newOrderItem: OrderItem = new OrderItem({})
      const newAddress: Address = new Address({ country: 'CZ' })

      orderPropertiesInArray.forEach((orderProperty, index2) => {
        switch (index2) {
          case 0:
            newOrder.dateExpedition = orderProperty
            return
          case 1:
            newOrder.externalReference = orderProperty
            return
          case 2:
            newOrder.reservationNumber = Number(orderProperty)
            return
          case 3:
            // cispovlistu skip
            return
          case 4:
            newOrderItem.code = orderProperty
            return
          case 5:
            newOrderItem.name = orderProperty
            return
          case 6:
            newOrderItem.ean = orderProperty ? parseInt(orderProperty) + '' : undefined
            return
          case 7:
            if (orderProperty.includes(',')) orderProperty = orderProperty.split(',').join('.')
            newOrderItem.quantity =
              typeof orderProperty === 'string' && orderProperty?.length
                ? Number(orderProperty)
                : newOrderItem.name
                  ? 1
                  : 0
            return
          case 8:
            // MJSkladova skip
            return
          case 9:
            newAddress.firstName = orderProperty
            return
          case 10:
            newAddress.lastName = orderProperty
            return
          case 11:
            newAddress.street = orderProperty
            return
          case 12:
            newAddress.city = orderProperty
            return
          case 13:
            newAddress.zip = orderProperty
            return
          case 14:
            newAddress.phone = orderProperty
            return
          case 15:
            if (orderProperty.includes(',')) orderProperty = orderProperty.split(',').join('.')
            newOrder.priceAll = orderProperty
            return
          case 16:
            newOrder.currency = orderProperty
            return
          case 17:
            newOrder.billNumber = orderProperty
            newOrder.privateNote = orderProperty
            return
          case 18:
            newOrder.note = orderProperty
            return
          case 19:
            newOrder.privateNote = newOrder.privateNote + '\n' + orderProperty
            return
          case 20:
            newOrder.privateNote = newOrder.privateNote + '\n' + orderProperty
            return
          case 21:
            // OZ skip
            return
          case 22:
            // VlastniOdber skip
            return
          case 23:
            // Expresni dodani skip
            return
        }
      })
      // set OrderItem
      if (newOrderItem.name?.length) {
        await this.setOrderItem(newOrderItem, newOrder)
      }

      // check if is not another line for same order
      const alreadyPushedOrder = orders.find(
        order => Number(order?.reservationNumber) === Number(orderPropertiesInArray[2])
      )

      if (alreadyPushedOrder) {
        alreadyPushedOrder.items.push(newOrderItem)
      } else {
        // set Address
        newOrder.invoiceAddress = newAddress
        newOrder.shippingAddress = newAddress
        // set paymentItem
        const paymentItem = new PaymentItem({
          payment:
            Number(newOrder.priceAll) === 0
              ? this.payments.find(p => p.type?.id === this.goPayment2Id)
              : this.payments.find(p => p.type?.id === this.goPaymentId),
          checkout: this.checkouts.find(c => c.id === this.selectedCheckout),
          amount: newOrder.priceAll,
        })
        if (newOrder.paymentItems && newOrder.paymentItems.length === 0) {
          newOrder.paymentItems.push(paymentItem)
        }
        orders.push(newOrder)
      }
    }
    return orders
  }

  async setOrderItem(newOrderItem, newOrder) {
    // check if the product exists
    let existingProduct
    if (newOrderItem.code || newOrderItem.ean) {
      const prop = newOrderItem.ean ? 'ean' : 'code'
      existingProduct = await this.findProduct(prop, newOrderItem)

      if (!existingProduct) {
        const candidate: ProductSearchField = prop === 'ean' ? 'code' : 'ean'
        if (newOrderItem[candidate]) {
          existingProduct = await this.findProduct(candidate, newOrderItem)
        }
      }
    }
    if (existingProduct) {
      newOrderItem.product = existingProduct
    }
    newOrderItem.vat = (this.queryVats.data() ?? []).find(v => v.default)
    newOrderItem.sale = 0
    newOrder.items.push(newOrderItem)
  }

  findProduct(prop: ProductSearchField, newOrderItem) {
    return this.findImportProduct(prop, newOrderItem[prop])
  }
}
